import { format } from 'date-fns';
import { clubApi } from '@repo/api-client';
import { Link, LoaderFunctionArgs, useLoaderData } from 'react-router';
import { urlControlPanel, urlTournament, urlTournamentList } from '@/urls';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/dialog';
import { CreateTournamentForm } from '@/components/tournaments/create-tournament-form';
import { Accordion } from '@/components/ui/accordion';
import { TournamentCard } from '@/components/tournaments/tournament-card';
import { EmptyState } from '@repo/ui/components/empty-state';
import { PlusIcon } from 'lucide-react';
import { Button } from '@repo/ui/components/button';
import type { Tournament } from '@repo/shared-types';

const isUpcomingOrCurrent = (t: Tournament) => t.status !== 'FINISHED';

function TournamentList() {
  const { tournaments, clubId } = useLoaderData<typeof loader>();

  const finalTournaments: Tournament[] = tournaments?.results.map((t) => ({
    ...t,
    startDate: format(new Date(t.startDate), 'dd/MM/yyyy'),
    endDate: format(new Date(t.endDate), 'dd/MM/yyyy'),
  }));

  const upcomingOrCurrentTournaments = finalTournaments.filter(isUpcomingOrCurrent);
  const pastTournaments = finalTournaments.filter((t) => !isUpcomingOrCurrent(t));
  const hasTournaments = upcomingOrCurrentTournaments.length > 0 || pastTournaments.length > 0;

  const createTournamentDialog = (
    <Dialog>
      <DialogTrigger>
        <Button>
          <PlusIcon className="w-4 h-4" />
          Crear Torneo
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Crear Torneo</DialogTitle>
        <DialogDescription>Completa los campos para crear un nuevo torneo</DialogDescription>
        <CreateTournamentForm clubId={clubId.toString()} />
      </DialogContent>
    </Dialog>
  );

  if (!hasTournaments) {
    return (
      <EmptyState
        title="No hay torneos disponibles"
        description="Crea un torneo para empezar"
        actions={createTournamentDialog}
      />
    );
  }

  return (
    <>
      {createTournamentDialog}
      {hasTournaments ? (
        <div className="flex flex-col gap-4 my-4">
          {upcomingOrCurrentTournaments.length > 0 && (
            <Accordion title="Actuales y próximos" defaultOpen>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {upcomingOrCurrentTournaments.map((t) => (
                  <Link
                    to={urlTournament(clubId.toString(), t.id.toString())}
                    viewTransition
                    key={t.id}
                  >
                    <TournamentCard tournament={t} />
                  </Link>
                ))}
              </div>
            </Accordion>
          )}
          {pastTournaments.length > 0 && (
            <Accordion title="Pasados">
              {pastTournaments.map((t) => (
                <Link
                  to={urlTournament(clubId.toString(), t.id.toString())}
                  viewTransition
                  key={t.id}
                >
                  <TournamentCard tournament={t} />
                </Link>
              ))}
            </Accordion>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-center font-medium">No hay torneos disponibles</p>
        </div>
      )}
    </>
  );
}

const loader = async ({ params }: LoaderFunctionArgs) => {
  const tournaments = await clubApi.getTournaments(params.id!);
  return { tournaments, clubId: params.id! };
};

export const route = {
  path: urlTournamentList(':id'),
  element: <TournamentList />,
  loader,
  handle: {
    backLink: (params: { id: string }) => urlControlPanel(params.id),
    title: 'Torneos',
  },
};
